import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { BellRing, CalendarClock, FileText, Loader2, Mail, MoonStar } from 'lucide-react'
import api from '../../services/api.js'

const KIND_ICON = { relance: BellRing, dormant: MoonStar, devis: FileText, email: Mail }
const BUCKETS = [
  { key: 'overdue', label: 'En retard', tone: 'text-red-400' },
  { key: 'today', label: "Aujourd'hui", tone: 'text-amber-400' },
  { key: 'week', label: '7 prochains jours', tone: 'text-ink' },
  { key: 'later', label: 'Plus tard', tone: 'text-foreground-secondary' },
]
const PRIORITY = {
  1: { label: 'P1', cls: 'bg-red-500/15 text-red-400' },
  2: { label: 'P2', cls: 'bg-amber-500/15 text-amber-400' },
  3: { label: 'P3', cls: 'bg-surface-active text-foreground-secondary' },
}

const dayStart = (d) => {
  const x = new Date(d)
  x.setHours(0, 0, 0, 0)
  return x
}
const daysFromToday = (iso) => Math.round((dayStart(iso) - dayStart(new Date())) / 864e5)
const money = (n) => (n ? `${Math.round(n).toLocaleString('fr-FR')} €` : '')

function dueLabel(action) {
  if (!action.dueAt) return null
  const diff = daysFromToday(action.dueAt)
  if (diff < 0) return { text: `En retard de ${-diff} j`, cls: 'text-red-400' }
  if (diff === 0) return { text: "Aujourd'hui", cls: 'text-amber-400' }
  if (diff === 1) return { text: 'Demain', cls: 'text-ink/70' }
  return { text: new Date(action.dueAt).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' }), cls: 'text-ink/70' }
}

function agendaDayLabel(iso) {
  const diff = daysFromToday(iso)
  if (diff === 0) return "Aujourd'hui"
  if (diff === 1) return 'Demain'
  return new Date(iso).toLocaleDateString('fr-FR', { weekday: 'short', day: 'numeric', month: 'short' })
}

function Tile({ label, value, hint, tone, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`flex min-w-0 flex-col gap-1.5 rounded-[14px] border bg-surface p-[18px] text-left transition hover:border-border-active ${active ? 'border-brand/60 ring-1 ring-brand/30' : 'border-border'}`}
    >
      <span className="text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-secondary">{label}</span>
      <span className={`text-[26px] font-semibold leading-none tracking-[-0.02em] ${tone}`}>{value}</span>
      <span className="text-xs text-foreground-muted">{hint}</span>
    </button>
  )
}

function ActionRow({ action }) {
  const Icon = KIND_ICON[action.kind] ?? CalendarClock
  const due = dueLabel(action)
  const prio = PRIORITY[action.priority] ?? PRIORITY[3]
  return (
    <li className={`flex items-center gap-3 border-l-2 px-3 py-3 ${action.bucket === 'overdue' ? 'border-l-red-500/70' : 'border-l-transparent'}`}>
      <span className={`shrink-0 rounded-md px-1.5 py-0.5 text-[11px] font-bold ${prio.cls}`}>{prio.label}</span>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-surface-active text-foreground-secondary">
        <Icon className="h-4 w-4" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-ink">{action.title}</p>
        <p className="truncate text-xs text-foreground-secondary">
          {[action.subtitle, money(action.amount)].filter(Boolean).join(' · ')}
        </p>
      </div>
      {due && <span className={`hidden shrink-0 text-xs font-medium sm:block ${due.cls}`}>{due.text}</span>}
      <Link
        to={action.link}
        className={`shrink-0 rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${
          action.kind === 'relance' || action.kind === 'dormant'
            ? 'bg-brand text-white hover:bg-brand-strong'
            : 'border border-border bg-surface-hover text-ink hover:border-border-active'
        }`}
      >
        {action.kind === 'devis' ? 'Finaliser' : action.kind === 'email' ? 'Voir' : 'Relancer'}
      </Link>
    </li>
  )
}

/** « Ma journée » (maquette Jean Louis V3) : priorités, relances, devis à finir et agenda 7 jours. */
export default function MyDay() {
  const [filter, setFilter] = useState(null) // overdue | today | week | urgent
  const [mode, setMode] = useState('due') // due | priority
  const { data, isLoading } = useQuery({
    queryKey: ['myDay'],
    queryFn: async () => (await api.get('/api/me/day')).data,
    staleTime: 30_000,
  })

  const counts = data?.counts ?? { overdue: 0, today: 0, week: 0, urgent: 0 }
  const actions = useMemo(() => {
    const all = data?.actions ?? []
    if (filter === 'urgent') return all.filter((a) => a.priority === 1)
    return filter ? all.filter((a) => a.bucket === filter) : all
  }, [data, filter])

  const groups = mode === 'priority'
    ? [1, 2, 3].map((p) => ({ key: `p${p}`, label: { 1: 'Urgent', 2: 'Important', 3: 'Normal' }[p], tone: p === 1 ? 'text-red-400' : p === 2 ? 'text-amber-400' : 'text-foreground-secondary', items: actions.filter((a) => a.priority === p) }))
    : BUCKETS.map((b) => ({ ...b, items: actions.filter((a) => a.bucket === b.key) }))

  const agendaByDay = useMemo(() => {
    const map = new Map()
    ;(data?.agenda ?? []).forEach((item) => {
      const key = dayStart(item.dueAt).toISOString()
      map.set(key, [...(map.get(key) ?? []), item])
    })
    return [...map.entries()]
  }, [data])

  const today = new Date().toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' })
  const parts = [
    counts.overdue ? <b key="o" className="text-red-400">{counts.overdue} action{counts.overdue > 1 ? 's' : ''} en retard</b> : null,
    counts.today ? <b key="t" className="text-ink">{counts.today} pour aujourd'hui</b> : null,
    counts.week ? <span key="w">{counts.week} dans les 7 jours</span> : null,
  ].filter(Boolean)

  return (
    <div className="flex flex-col gap-5">
      <section className="rounded-[14px] border border-border bg-surface p-6">
        <p className="text-xs font-semibold uppercase tracking-[0.12em] text-foreground-secondary">{today}</p>
        <h2 className="mt-2 text-xl font-bold text-ink">Bonjour {data?.firstName || ''}</h2>
        <p className="mt-1 text-sm text-foreground-secondary">
          {isLoading ? 'Chargement…' : parts.length ? parts.reduce((acc, el, i) => (i ? [...acc, i === parts.length - 1 ? ' et ' : ', ', el] : [el]), []) : 'Rien en attente : tout est à jour.'}
          {!isLoading && parts.length ? '.' : ''}
        </p>
      </section>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <Tile label="En retard" value={counts.overdue} hint="à rattraper" tone="text-red-400" active={filter === 'overdue'} onClick={() => setFilter(filter === 'overdue' ? null : 'overdue')} />
        <Tile label="Aujourd'hui" value={counts.today} hint="à faire dans la journée" tone="text-amber-400" active={filter === 'today'} onClick={() => setFilter(filter === 'today' ? null : 'today')} />
        <Tile label="7 prochains jours" value={counts.week} hint="relances prévues" tone="text-ink" active={filter === 'week'} onClick={() => setFilter(filter === 'week' ? null : 'week')} />
        <Tile label="Urgentes" value={counts.urgent} hint="priorité P1" tone="text-red-400" active={filter === 'urgent'} onClick={() => setFilter(filter === 'urgent' ? null : 'urgent')} />
      </section>

      <div className="grid items-start gap-4 xl:grid-cols-[minmax(0,1fr)_300px]">
        <section className="rounded-[14px] border border-border bg-surface p-5">
          <div className="mb-3 inline-flex rounded-full bg-surface-active p-[3px]" role="radiogroup" aria-label="Regrouper">
            {[{ v: 'due', l: 'Par échéance' }, { v: 'priority', l: 'Par priorité' }].map((o) => (
              <button
                key={o.v}
                type="button"
                role="radio"
                aria-checked={mode === o.v}
                onClick={() => setMode(o.v)}
                className={`rounded-full px-3.5 py-1.5 text-xs font-semibold transition ${mode === o.v ? 'bg-surface text-ink shadow-sm' : 'text-foreground-secondary hover:text-ink'}`}
              >
                {o.l}
              </button>
            ))}
          </div>

          {isLoading ? (
            <div className="flex justify-center py-10 text-foreground-muted"><Loader2 className="h-5 w-5 animate-spin" /></div>
          ) : actions.length === 0 ? (
            <p className="py-10 text-center text-sm text-foreground-muted">
              {filter ? 'Aucune action dans ce filtre.' : 'Aucune action : vos relances et vos devis sont à jour.'}
            </p>
          ) : (
            groups.filter((g) => g.items.length).map((group) => (
              <div key={group.key} className="mt-3 first:mt-1">
                <h3 className={`mb-1 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.08em] ${group.tone}`}>
                  {group.label}
                  <span className="rounded-full bg-surface-active px-2 py-0.5 text-[10px] text-foreground-secondary">{group.items.length}</span>
                </h3>
                <ul className="divide-y divide-border rounded-[10px] border border-border">
                  {group.items.map((action) => <ActionRow key={action.id} action={action} />)}
                </ul>
              </div>
            ))
          )}
        </section>

        <aside className="rounded-[14px] border border-border bg-surface p-5">
          <h3 className="mb-3 text-sm font-bold text-ink">Agenda · 7 jours</h3>
          {agendaByDay.length === 0 ? (
            <p className="text-xs text-foreground-muted">Rien de prévu cette semaine.</p>
          ) : (
            agendaByDay.map(([day, items]) => (
              <div key={day} className="mb-3">
                <p className="mb-1.5 text-[11px] font-semibold uppercase tracking-[0.08em] text-foreground-secondary">{agendaDayLabel(day)}</p>
                <ul className="space-y-1.5">
                  {items.map((item) => (
                    <li key={item.id}>
                      <Link to={item.link} className="flex gap-2.5 rounded-[10px] border border-dashed border-border px-2.5 py-2 transition hover:border-border-active">
                        <span className="w-10 shrink-0 text-[11px] text-foreground-muted">{item.time || 'à caler'}</span>
                        <span className="min-w-0">
                          <span className="block truncate text-xs font-semibold text-ink">{item.title}</span>
                          <span className="block truncate text-[11px] text-foreground-secondary">{item.subtitle}</span>
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))
          )}
        </aside>
      </div>
    </div>
  )
}
